import { Typography } from '@cast/design-system';
import {
  AggressiveModeConfig,
  ClusterStatus,
  KentRebalancingPlanStatusEnum,
  RebalancingPlanStatusEnum,
} from '@cast/types';
import { KentAggressiveModeConfig } from '@cast/types-kent';
import { ReactNode } from 'react';

import { DerivedRebalancingPlanStatus } from '../_types';

export const CLUSTER_STATES_AVAILABLE_FOR_REBALANCE = [
  ClusterStatus.READY,
  ClusterStatus.WARNING,
];

export const AUTOSCALER_REBALANCING_PLAN_STATUS_LABELS: Record<
  RebalancingPlanStatusEnum,
  string
> = {
  [RebalancingPlanStatusEnum.finished]: 'Completed',
  [RebalancingPlanStatusEnum.partially_finished]: 'Partial',
  [RebalancingPlanStatusEnum.creating_nodes]: 'In progress',
  [RebalancingPlanStatusEnum.draining_nodes]: 'In progress',
  [RebalancingPlanStatusEnum.deleting_nodes]: 'In progress',
  [RebalancingPlanStatusEnum.preparing_nodes]: 'In progress',
  [RebalancingPlanStatusEnum.error]: 'Failed',
  [RebalancingPlanStatusEnum.generating]: 'Generating',
  [RebalancingPlanStatusEnum.generated]: 'Ready',
  [RebalancingPlanStatusEnum.invalid]: 'Invalid',
};

export const KENT_REBALANCING_PLAN_STATUS_LABELS: Record<
  KentRebalancingPlanStatusEnum,
  string
> = {
  [KentRebalancingPlanStatusEnum.COMPLETED]: 'Completed',
  [KentRebalancingPlanStatusEnum.PARTIALLY_COMPLETED]: 'Partial',
  [KentRebalancingPlanStatusEnum.EXECUTING]: 'In progress',
  [KentRebalancingPlanStatusEnum.FAILED]: 'Failed',
  [KentRebalancingPlanStatusEnum.GENERATING]: 'Generating',
  [KentRebalancingPlanStatusEnum.READY]: 'Ready',
  [KentRebalancingPlanStatusEnum.SKIPPED]: 'Skipped',
  [KentRebalancingPlanStatusEnum.CANCELED]: 'Canceled',
  [KentRebalancingPlanStatusEnum.STALE]: 'Stale',
  [KentRebalancingPlanStatusEnum.EXPIRED]: 'Expired',
  [KentRebalancingPlanStatusEnum.REBALANCING_PLAN_STATUS_UNSPECIFIED]:
    'Unknown',
};

export const DERIVED_REBALANCING_PLAN_STATUS_LABELS: Record<
  DerivedRebalancingPlanStatus,
  string
> = {
  obsolete: 'Obsolete',
};

export const MAX_DRAIN_TIMEOUT_MINUTES = 60;
export const MIN_DRAIN_TIMEOUT_MINUTES = 1;
export const DEFAULT_DRAIN_TIMEOUT_MINUTES = 60;

export const DEFAULT_AGGRESSIVE_MODE_CONFIG: AggressiveModeConfig = {
  ignoreLocalPersistentVolumes: false,
  ignoreProblemJobPods: true,
  ignoreProblemRemovalDisabledPods: true,
  ignoreProblemPodsWithoutController: true,
  ignoreProblemPreventedDrainPods: false,
};

export const DEFAULT_MAX_SIMULTANEOUS_DRAINS = 5;
export const DEFAULT_SPLIT_NODES_IN_BATCHES_OF = 5;

export const BATCH_MODE_DESCRIPTION =
  'Run the rebalance in batches, limiting how many nodes drain at once and how many extra nodes exist at any moment.';

export const BATCH_MODE_MAX_SIMULTANEOUS_DRAINS_DESCRIPTION =
  'Number of nodes drained at the same time. Leave empty for unlimited.';

export const BATCH_MODE_SPLIT_NODES_IN_BATCHES_OF_DESCRIPTION =
  'Limits how many additional nodes (nodes created minus nodes drained) exist at any moment. A node drains only after its replacement is ready. Zero or empty runs the plan in a single batch.';

export const DEFAULT_KENT_AGGRESSIVE_MODE_CONFIG: KentAggressiveModeConfig = {
  aggressiveEviction: false,
  ignoreBlockingPdb: true,
  ignoreConsolidatable: true,
  ignoreInstanceCriteria: false,
  ignoreLocalPersistentVolumes: false,
  ignoreProblemJobPods: true,
  ignoreProblemRemovalDisabledNodes: false,
  ignoreProblemRemovalDisabledPods: true,
  ignoreProblemPodsWithoutController: true,
  ignoreZeroNodePoolDisruptionBudgets: false,
};

export const AGGRESSIVE_MODE_OPTION_LABELS = {
  ignoreProblemRemovalDisabledNodes: 'Nodes marked with "Removal Disabled"',
  ignoreConsolidatable: 'Nodes not marked Consolidatable by Karpenter',
  aggressiveEviction: 'Enable forced pod deletion on drain timeout',
  ignoreInstanceCriteria: 'Find the most cost-optimal setup',
  ignoreBlockingPdb: 'PDB-blocked pods',
  ignoreZeroNodePoolDisruptionBudgets:
    'Ignore NodePool zero-disruption budgets',
  ignoreProblemPreventedDrainPods: 'Pods that previously failed to drain',
  ignoreLocalPersistentVolumes: 'Pods with local persistent volumes attached',
  ignoreRemovalDisabledPods: 'Pods marked with "Removal Disabled"',
  ignorePodsWithoutController: 'Pods without controllers (bare pods)',
  ignoreJobPods: 'Job pods',
};

export const KENT_AGGRESSIVE_MODE_OPTION_TOOLTIP_TEXTS = {
  ignoreLocalPersistentVolumes: (
    <Typography>
      <Typography component="span" fontWeight="medium">
        Warning: may cause data loss.
      </Typography>{' '}
      Local PV data is destroyed when the node is deleted. By default, pods with
      local PVs block rebalancing.
    </Typography>
  ),
  ignoreBlockingPdb:
    'Allows rebalancing to proceed even when a PodDisruptionBudget currently allows 0 disruptions. Only effective when forced pod deletion is enabled and a drain timeout is set.',
  ignoreProblemJobPods:
    'Allows rebalancing of pods owned by a Job or CronJob. Partial job progress is lost when the pod is terminated mid-execution.',
  ignoreProblemPodsWithoutController:
    'Allows rebalancing of pods with no owner reference. They may not restart after eviction because no controller will recreate them.',
  ignoreProblemRemovalDisabledPods: (
    <Typography>
      Bypasses explicit removal protection labels/annotations (e.g.{' '}
      <Typography component="span" variant="C12">
        karpenter.sh/do-not-disrupt
      </Typography>
      ,{' '}
      <Typography component="span" variant="C12">
        cluster-autoscaler.kubernetes.io/safe-to-evict=false
      </Typography>
      ). By default these pods block rebalancing.
    </Typography>
  ),
  ignoreZeroNodePoolDisruptionBudgets:
    'Ignores NodePool disruption budgets that currently allow 0 disruptions. Rebalancing proceeds as if the default 10% safety budget applied.',
  ignoreProblemRemovalDisabledNodes:
    'Bypasses node-level removal protection labels/annotations (e.g. karpenter.sh/do-not-disrupt). By default such nodes block rebalancing.',
  ignoreConsolidatable:
    'Bypasses the Karpenter Consolidatable condition check during node candidate selection. Nodes that Karpenter has not yet marked as consolidatable (e.g. the ConsolidateAfter time window has not elapsed) will still be considered for rebalancing.',
  drainTimeoutSeconds: `Maximum time allowed for draining a node before forced termination. Valid range: ${MIN_DRAIN_TIMEOUT_MINUTES}–${MAX_DRAIN_TIMEOUT_MINUTES} minutes. Short timeouts may kill pods mid-termination and cause data loss.`,
  aggressiveEviction:
    'Enables forced termination of NodeClaims after the drain timeout expires. Without this, drains are graceful and PDBs are honored. Stateful workloads without clean shutdown handling may lose data.',
  ignoreInstanceCriteria:
    'Removes NodePool instance-type constraints during plan generation so the placer can pick from a broader set. The selected type may not match your workload requirements.',
} satisfies Partial<Record<keyof KentAggressiveModeConfig, ReactNode>>;
